import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const FollowSuggestionsPage = () => {
  const navigate               = useNavigate();
  const { authToken }          = useContext(AuthContext);
  const [suggestions, setSuggestions] = useState([]);
  const [followed, setFollowed]       = useState({});   // twitch_id → bool
  const [loading, setLoading]         = useState(true);
  const [followingAll, setFollowingAll] = useState(false);

  useEffect(() => {
    if (!authToken) { navigate('/account'); return; }
    fetch(`${BACKEND_URL}/api/follows/suggestions`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.json())
      .then(d => {
        const list = d.suggestions || [];
        // Skip the screen entirely when there are no matches
        if (list.length === 0) { navigate('/account'); return; }
        setSuggestions(list);
        const initial = {};
        list.forEach(u => { initial[u.twitch_id] = u.is_following; });
        setFollowed(initial);
      })
      .catch(() => navigate('/account'))
      .finally(() => setLoading(false));
  }, [authToken, navigate]);

  const toggleFollow = async (twitch_id) => {
    const isFollowing = followed[twitch_id];
    setFollowed(prev => ({ ...prev, [twitch_id]: !isFollowing }));
    try {
      await fetch(`${BACKEND_URL}/api/follows/${twitch_id}`, {
        method:  isFollowing ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch {
      // Revert on failure
      setFollowed(prev => ({ ...prev, [twitch_id]: isFollowing }));
    }
  };

  const followAll = async () => {
    setFollowingAll(true);
    const unfollowed = suggestions.filter(u => !followed[u.twitch_id]);
    await Promise.allSettled(
      unfollowed.map(u =>
        fetch(`${BACKEND_URL}/api/follows/${u.twitch_id}`, {
          method:  'POST',
          headers: { Authorization: `Bearer ${authToken}` },
        })
      )
    );
    const next = { ...followed };
    unfollowed.forEach(u => { next[u.twitch_id] = true; });
    setFollowed(next);
    setFollowingAll(false);
  };

  const allFollowed = suggestions.length > 0 && suggestions.every(u => followed[u.twitch_id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e5e5e5', borderTopColor: '#000', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontFamily: '"Barlow Condensed", sans-serif', letterSpacing: '.05em', fontSize: '.85rem', color: '#999' }}>LOADING</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1.5rem 4rem' }}>
      {/* Header */}
      <div style={{ maxWidth: 540, width: '100%', textAlign: 'center', marginBottom: '2.5rem' }}>
        <p style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '2rem', letterSpacing: '-.02em', margin: '0 0 .5rem' }}>
          PEOPLE YOU ALREADY KNOW
        </p>
        <p style={{ color: '#666', fontSize: '.9rem', margin: 0 }}>
          These streamers are on BGN and you already follow them on Twitch.
        </p>
      </div>

      {/* User grid */}
      <div style={{ maxWidth: 540, width: '100%', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: '2rem' }}>
        {suggestions.map(user => (
          <div
            key={user.twitch_id}
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', border: '1px solid #e5e5e5', background: '#fafafa' }}
          >
            {user.profile_image_url
              ? <img src={user.profile_image_url} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              : <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#e5e5e5', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.9rem' }}>
                  {(user.display_name || user.login)[0].toUpperCase()}
                </div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.display_name || user.login}
                {user.is_verified && <span style={{ marginLeft: 6, fontSize: '.65rem', background: '#000', color: '#ffd700', padding: '1px 5px', fontFamily: '"Barlow Condensed", sans-serif', letterSpacing: '.05em' }}>BGN</span>}
              </p>
              <p style={{ margin: 0, fontSize: '.8rem', color: '#888' }}>@{user.login}</p>
            </div>
            <button
              onClick={() => toggleFollow(user.twitch_id)}
              style={{
                padding: '7px 18px',
                fontSize: '.8rem',
                fontWeight: 700,
                fontFamily: '"Barlow Condensed", sans-serif',
                letterSpacing: '.04em',
                cursor: 'pointer',
                border: '1.5px solid #000',
                background: followed[user.twitch_id] ? '#000' : '#fff',
                color:      followed[user.twitch_id] ? '#fff' : '#000',
                transition: 'background .15s, color .15s',
                flexShrink: 0,
              }}
            >
              {followed[user.twitch_id] ? 'FOLLOWING' : 'FOLLOW'}
            </button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ maxWidth: 540, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!allFollowed && (
          <button
            onClick={followAll}
            disabled={followingAll}
            style={{ width: '100%', padding: '13px', background: '#000', color: '#ffd700', border: 'none', fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 900, fontSize: '1rem', letterSpacing: '.06em', cursor: followingAll ? 'default' : 'pointer', opacity: followingAll ? .6 : 1 }}
          >
            {followingAll ? 'FOLLOWING ALL…' : 'FOLLOW ALL'}
          </button>
        )}
        <button
          onClick={() => navigate('/account')}
          style={{ width: '100%', padding: '13px', background: '#fff', color: '#000', border: '1.5px solid #e5e5e5', fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '.9rem', letterSpacing: '.04em', cursor: 'pointer' }}
        >
          {allFollowed ? 'CONTINUE' : 'SKIP'}
        </button>
      </div>
    </div>
  );
};

export default FollowSuggestionsPage;
